import { AppStaticURL } from '@app/app.settings';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NewsService } from '@app/modules/news-modules/news.service';
import { SEOService } from '@app/core/services';
import { DevicesClass } from '@app/core/classes';
import { NewsViewTypes } from '@app/shared/news-item/news-item.component';
import { BreadcrumbsDataModel } from '@app/core/models/breadcrumbs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RoutingConfig } from '@app/routing/routing.config';
import { PrimeNGConfig } from 'primeng/api';
import { FlattenObject } from '@app/utils/converter';
import { DialogIncludesTypes } from '@app/core/models/diallog';
import { NotificationService } from '@app/modules/notification-modules/notification.service';
import { IBlogPostNewsDetail, IBlogPostRequest } from '@app/modules/news-modules/news.models';
import { AccountEventBus, AccountStoryService } from '@app/modules/account-modules/account.story';
declare var $;

export interface BaseErrorModel {
  status: boolean;
  value: string;
}
export interface CreateNewsErrorModels {
  title: BaseErrorModel;
  text: BaseErrorModel;
  picPath: BaseErrorModel;
  thumbnailPath: BaseErrorModel;
}

@UntilDestroy()
@Component({
  selector: 'app-news-crud',
  templateUrl: 'news-crud.component.html',
  styleUrls: [`./news-crud.component.scss`],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class NewsCrudComponent extends DevicesClass implements OnInit {
  public StaticURL = AppStaticURL;
  public NVType = NewsViewTypes;
  public BreadcrumbsData: BreadcrumbsDataModel = null;

  /**
   * Base component variables
   */
  public NewsCrudForm: FormGroup;
  public NewsCrudLoading: boolean;
  public NewsCrudPreview: boolean;
  public NewsCrudDetail: IBlogPostNewsDetail;
  public NewsTitleLimit: number;
  public NewsCrudLimit: number;

  /**
   * Special component variables.
   */
  public fileAcceptString = 'image/x-png,image/gif,image/jpeg';

  /**
   * Errors view variables
   */
  public ErrorsField: CreateNewsErrorModels;
  public ErrorsText: string;

  constructor(injector: Injector,
    public cdr: ChangeDetectorRef,
    private seo: SEOService,
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private accountStory: AccountStoryService,
    private newsService: NewsService,
    private primengConfig: PrimeNGConfig,
    private notificationService: NotificationService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.primengConfig.ripple = true;

    /**
     * Build NewsCrud form.
     */
    this.NewsCrudForm = this.formBuilder.group({
      title: ['', Validators.required],
      text: ['', Validators.required],
      picPath: ['', Validators.required],
      thumbnailPath: ['', Validators.required],
      isNews: [true],
    });

    /**
     * Set of default errors value.
     */
    this.ErrorsField = {
      title: {status: false, value: 'Обязательное поле для заполнения'},
      text: {status: false, value: 'Обязательное поле для заполнения'},
      picPath: {status: false, value: 'Обязательное поле для заполнения'},
      thumbnailPath: {status: false, value: ''}
    };

    /**
     * Form changes control.
     */
    this.onNewsCrudFormCtrl();
    this.onNewsTextCtrl();
    this.onNewsTitleCtrl();

    /**
     * Account story.
     */
    this.onAccountStoryBus();

    /**
     * Set of storage values.
     */
    this.route.paramMap.subscribe(
      value => {
        const Id = value.get('id');

        this.BreadcrumbsData = [
          {
            title: 'Новсти',
            url: '/' + RoutingConfig.News,
          },
          {
            title: Id ? 'Редактировать новость' : 'Создать новость',
            url: '/' + RoutingConfig.News + '/' + RoutingConfig.NewsCreate,
            active: true
          }
        ];

        Id ? this.onSetNewDetail(Id) : this.onSetStorageValue();
      }
    );
  }

  onAccountStoryBus(): void {
    this.accountStory.AccountEventBus$.pipe(untilDestroyed(this)).subscribe(
      emit => this.onAccountStoryListener(emit)
    );
  }

  onAccountStoryListener(emit: AccountEventBus): void {
    if (emit.event === 'onAccountLogout' || emit.event === 'onAccountEmpty') {
      this.router.navigateByUrl(`/${RoutingConfig.News}`).catch();
    }
  }

  onSetStorageValue(inputDate?: IBlogPostNewsDetail): void {
    const NCStorage = inputDate || FlattenObject(this.newsService.getNewsFormData());

    const loopControls = (controls) => {
      for (const key of Object.keys(controls)) {
        if (controls[key] instanceof FormControl) {
          if (NCStorage.hasOwnProperty(key)) {

            if ((key === 'picPath' || key === 'thumbnailPath') && NCStorage[key] !== '') {
              controls[key].setValue(NCStorage[key]);
              this.NewsCrudPreview = true;
              this.cdr.markForCheck();
              continue;
            }

            controls[key].setValue(NCStorage[key]);
          }
        } else {
          loopControls(controls[key].controls);
        }
      }
    };
    loopControls(this.NewsCrudForm.controls);
  }

  onSetNewDetail(id: string): void {
    this.newsService.getNewsById(Number(id)).subscribe(
      response => {
        this.NewsCrudDetail = response;


        response.picPath = response.picUrl;
        this.onSetStorageValue(response);
      }
    )
  }

  onNewsCrudFormCtrl(): void {
    this.NewsCrudForm.valueChanges.pipe(untilDestroyed(this)).subscribe(
      value => {
        if (!this.NewsCrudDetail) {
          this.newsService.saveNewsFormData(this.NewsCrudForm.getRawValue())
        }
      }
    );

    for (const key of Object.keys(this.ErrorsField)) {
      if (this.NewsCrudForm.controls.hasOwnProperty(key)) {
        this.NewsCrudForm.controls[key].valueChanges.pipe(untilDestroyed(this)).subscribe(
          v => {
            this.ErrorsField[key].status = false;
            this.ErrorsText = '';

            this.onToggleViewErrors(false);
          }
        )
      }
    }
  }

  onNewsTextCtrl(): void {
    this.NewsCrudForm.controls['text'].valueChanges.pipe(untilDestroyed(this)).subscribe(
      value => {
        if (value && value.length > 10000) {
          this.ErrorsField.text.status = true;
          this.ErrorsField.text.value = 'Превышен лимит символов'
        }

        this.NewsCrudLimit = value ? 10000 - value.length : 10000;
        this.cdr.markForCheck();
      }
    );
  }

  onNewsTitleCtrl(): void {
    this.NewsCrudForm.controls['title'].valueChanges.pipe(untilDestroyed(this)).subscribe(
      value => {
        if (value && value.length > 100) {
          this.ErrorsField.text.status = true;
          this.ErrorsField.text.value = 'Превышен лимит символов'
        }

        this.NewsTitleLimit = value ? 100 - value.length : 100;
        this.cdr.markForCheck();
      }
    );
  }

  onNewsCrudLoading(status: boolean, delay: number = 0) {
    setTimeout(() => {
      this.NewsCrudLoading = status;
      this.cdr.detectChanges();
    }, delay);
  }

  onUploadPreviewHandler(): void {
    const UploadInput = document.getElementById('previewUpload');
    UploadInput.click();
  }

  onUploadPreviewEvent(files: FileList): void {
    for (const i of Object.keys(files)) {
      const File: File = files[i];

      if ((File.size / 1024) > 1024) {
        return this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupErrorTemplate,
          header: 'Ошибка загрузки',
          content: 'Размер файла слишком большой.<br/> Ограничение по размеру - 2Mb',
        });
      }

      this.newsService.uploadImage(files).subscribe(
        response => {
          this.NewsCrudPreview = true;
          this.NewsCrudForm.controls.picPath.setValue(response.picPath);
          this.NewsCrudForm.controls.thumbnailPath.setValue(response.thumbnailPath);
          this.cdr.markForCheck();
        },
        error => {
          this.notificationService.onDialog({
            type: DialogIncludesTypes.PopupErrorTemplate,
            header: 'Ошибка загрузки',
            content: 'Не удалось загрузить изображение новости. Попробуйте использовать другой файл.',
          });
        }
      );
    }
  }

  onRemovePreview(): void {
    this.NewsCrudForm.controls.picPath.setValue('');
    this.NewsCrudForm.controls.thumbnailPath.setValue('');
    this.NewsCrudPreview = false;
    this.cdr.markForCheck();
  }

  onToggleViewErrors(show: boolean): void {
    const NewsCrudErrors = $('.__news_crud__errors');
    show ? NewsCrudErrors.slideDown(150) : NewsCrudErrors.slideUp(150);
  }

  onSubmitForm(): void {
    /**
    * Loading.
    */
    if (this.NewsCrudLoading) {
      return;
    }


    /**
     * Validation of Errors Fields.
     */
    for (const field of Object.keys(this.ErrorsField)) {
      if (this.ErrorsField[field].status) {
        this.ErrorsText = 'Проверьте правильность заполненных полей!';
        return this.onToggleViewErrors(true);
      }
    }


    /**
     * Initial validation.
     */
    if (!this.NewsCrudForm.valid) {
      for (const key of Object.keys(this.NewsCrudForm.controls)) {
        if (this.ErrorsField.hasOwnProperty(key)) {
          this.ErrorsField[key].status = !this.NewsCrudForm.controls[key].valid;
        }
      }
      this.ErrorsText = 'Заполните все обязательные поля!';

      this.onToggleViewErrors(true);
      return;
    }


    /**
     * Loading.
     */
    this.onNewsCrudLoading(true);


    /**
     * NewCreate request API.
     */
    const NewCreateValue = <IBlogPostRequest>this.NewsCrudForm.getRawValue();

    this.NewsCrudDetail ? this.onEditNewsAPI(NewCreateValue) : this.onCreateNewsAPI(NewCreateValue);


  }

  onCreateNewsAPI(request: IBlogPostRequest): void {
    this.newsService.createNews(request).subscribe(
      r => {
        this.onRequestCallBackSuccess();

        this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupSuccessTemplate,
          header: 'Готово',
          content: 'Новость успешно создана.',
        });
      },
      e => {
        this.onNewsCrudLoading(false);

        this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupErrorTemplate,
          header: 'Ошибка',
          content: 'Не удалось создать новость',
        });
      }
    )
  }

  onEditNewsAPI(request: IBlogPostRequest): void {
    this.newsService.updateNews(this.NewsCrudDetail.id, request).subscribe(
      r => {
        this.onRequestCallBackSuccess();

        this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupSuccessTemplate,
          header: 'Готово',
          content: 'Новость успешно обновлена.',
        });
      },
      e => {
        this.onNewsCrudLoading(false);

        this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupErrorTemplate,
          header: 'Ошибка',
          content: 'Не удалось изменить новость',
        });
      }
    )
  }

  onRequestCallBackSuccess(): void {
    /**
     * Clear storage formData.
     */
    this.newsService.clearNewsFormData();

    /**
     * Clear loading.
     */
    this.onNewsCrudLoading(false);

    /**
     * Navigate to NewsLists
     */
    this.router.navigateByUrl(`/${RoutingConfig.News}`).catch();
  }
}
